// src/components/admin/AdminSidebar.js
import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronDown, ChevronRight } from "lucide-react";

const SECTIONS = [
  {
    title: "온라인지원 관리",
    items: [{ label: "온라인지원 목록", to: "/admin/contact/list" }],
  },
  {
    title: "채용공고 관리",
    items: [
      { label: "채용공고 목록", to: "/admin/employment/list" },
      { label: "채용공고 등록", to: "/admin/employment/new" },
      { label: "접수 내역", to: "/admin/employment/inquiry/list" },
    ],
  },
  {
    title: "뉴스&이벤트 관리",
    items: [
      { label: "뉴스&이벤트 목록", to: "/admin/news/list" },
      { label: "뉴스&이벤트 등록", to: "/admin/news/new" },
    ],
  },
  {
    title: "관리자계정 관리",
    items: [
      { label: "관리자계정 목록", to: "/admin/user/list" },
      { label: "관리자계정 등록", to: "/admin/user/register" },
    ],
  },
];

// ✅ 한 곳에서만 active 여부 계산
function isItemActive(it, pathname) {
  // 1) 정확히 일치 or 하위경로면 active
  let active = pathname === it.to || pathname.startsWith(it.to + "/");

  // 2) "/list"는 "숫자 상세(/:id)"일 때만 목록을 active로 보정
  if (!active && it.to.endsWith("/list")) {
    const base = it.to.replace(/\/list$/, ""); // 예: /admin/employment

    if (pathname.startsWith(base + "/")) {
      const tail = pathname.slice(base.length + 1).split("/")[0]; // 예: "1", "new", "inquiry" ...

      // ✅ 숫자일 때만 상세로 인정
      if (/^\d+$/.test(tail)) {
        active = true;
      }
    }
  }

  return active;
}

export default function AdminSidebar({ onNavigate }) {
  const { pathname } = useLocation();
  const [open, setOpen] = useState({});

  // 🔹 onNavigate가 있으면 모바일 오버레이에서 쓰는 것으로 간주
  const isOverlay = !!onNavigate;

  const toggle = (i) => setOpen((p) => ({ ...p, [i]: !p[i] }));

  const handleClickItem = () => {
    if (onNavigate) onNavigate(); // 모바일 오버레이일 때 메뉴 클릭 후 닫기
  };

  // 현재 경로 기준으로 자동 펼침
  useEffect(() => {
    const nextOpen = {};
    SECTIONS.forEach((sec, idx) => {
      const hasActiveItem = sec.items.some((it) => isItemActive(it, pathname));
      nextOpen[idx] = hasActiveItem;
    });
    if (!Object.values(nextOpen).some(Boolean)) {
      nextOpen[0] = true;
    }
    setOpen((prev) => ({ ...prev, ...nextOpen }));
  }, [pathname]);

  const asideClass = isOverlay
    ? // 🔹 모바일 오버레이용
      "w-full max-w-xs h-full"
    : // 🔹 데스크탑 고정 사이드바
      "hidden shrink-0 md:block md:w-[260px] lg:w-[300px] xl:w-[340px]";

  return (
    <aside className={asideClass}>
      <div className="h-full rounded-2xl border bg-white p-4 shadow-sm overflow-y-auto">
        <h2 className="px-2 pb-2 text-lg font-extrabold text-blue-900 whitespace-nowrap">
          관리자 메뉴
        </h2>
        <nav className="divide-y">
          {SECTIONS.map((sec, idx) => (
            <div key={sec.title} className="py-2">
              <button
                type="button"
                onClick={() => toggle(idx)}
                className="flex w-full items-center justify-between rounded-lg px-2 py-2 text-left text-[15px] font-semibold text-slate-800 hover:bg-slate-50"
              >
                <span className="whitespace-nowrap">{sec.title}</span>
                {open[idx] ? (
                  <ChevronDown className="h-4 w-4 text-slate-500" />
                ) : (
                  <ChevronRight className="h-4 w-4 text-slate-500" />
                )}
              </button>

              {open[idx] && (
                <ul className="mt-1 space-y-1">
                  {sec.items.map((it) => {
                    const active = isItemActive(it, pathname);
                    return (
                      <li key={it.label}>
                        <Link
                          to={it.to}
                          onClick={handleClickItem}
                          className={`flex items-center gap-2 rounded-lg px-3 py-2 text-sm ${
                            active
                              ? "bg-blue-600/10 text-blue-700"
                              : "text-slate-700 hover:bg-slate-50"
                          }`}
                        >
                          <span
                            className={`h-1.5 w-1.5 rounded-full ${
                              active ? "bg-blue-600" : "bg-slate-300"
                            }`}
                          />
                          <span className="whitespace-nowrap">{it.label}</span>
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          ))}
        </nav>
      </div>
    </aside>
  );
}
